import { currentTimeZone, type JourneyNode } from './node';

export type GraphSchemaVersion = '1.0.0' | '2.0.0';

export const RELATIONSHIP_GRAPH_SCHEMA_VERSION: GraphSchemaVersion = '2.0.0';

export interface GraphNodeAnchor {
  x: number;
  y: number;
  z: number;
}

export interface GraphNodeOffset {
  x: number;
  y: number;
  z?: number;
}

export interface GraphLayoutSnapshot {
  nodeAnchors: Record<string, GraphNodeAnchor>;
}

export interface AutoConnection {
  connectionId: string;
  sourceNodeId: string;
  targetNodeId: string;
  reasonCode: string;
  reasonSummary: string;
  derivedFromFields: string[];
  strengthScore: number;
  createdAt: string;
  createdAtTimeZone?: string;
  updatedAt: string;
  updatedAtTimeZone?: string;
}

export interface RelationshipDirectionSet {
  sourceNodeId: string;
  targetNodeId: string;
  labels: string[];
}

export interface RelationshipPair {
  pairId: string;
  endpoints: [string, string];
  directions: [RelationshipDirectionSet, RelationshipDirectionSet];
  createdAt: string;
  updatedAt: string;
  createdAtTimeZone?: string;
  updatedAtTimeZone?: string;
}

export interface GraphDocument {
  graphId: string;
  title: string;
  description?: string;
  schemaVersion: GraphSchemaVersion;
  ownerMode: 'single-owner';
  timestampOriginTimeZone?: string;
  createdAtTimeZone?: string;
  updatedAtTimeZone?: string;
  layout: GraphLayoutSnapshot;
  nodes: JourneyNode[];
  connections: AutoConnection[];
  relationships?: RelationshipPair[];
  createdAt: string;
  updatedAt: string;
}
export function createEmptyGraph(title = 'Untitled Journey'): GraphDocument {
  const now = new Date().toISOString();
  const timeZone = currentTimeZone();
  return {
    graphId: `graph-${cryptoLikeId()}`,
    title,
    schemaVersion: RELATIONSHIP_GRAPH_SCHEMA_VERSION,
    ownerMode: 'single-owner',
    timestampOriginTimeZone: timeZone,
    createdAtTimeZone: timeZone,
    updatedAtTimeZone: timeZone,
    layout: createEmptyGraphLayout(),
    nodes: [],
    connections: [],
    relationships: [],
    createdAt: now,
    updatedAt: now,
  };
}
export function cryptoLikeId(): string { return Math.random().toString(36).slice(2, 10); }

export function inferGraphTimeZone(
  graph: Pick<Partial<GraphDocument>, 'timestampOriginTimeZone' | 'createdAtTimeZone' | 'updatedAtTimeZone' | 'nodes'>,
): string | undefined {
  return graph.timestampOriginTimeZone
    ?? graph.updatedAtTimeZone
    ?? graph.createdAtTimeZone
    ?? graph.nodes?.find((node) => node.updatedAtTimeZone)?.updatedAtTimeZone
    ?? graph.nodes?.find((node) => node.createdAtTimeZone)?.createdAtTimeZone;
}

export function createEmptyGraphLayout(): GraphLayoutSnapshot {
  return { nodeAnchors: {} };
}

export function normalizeGraphRelationships(
  relationships: RelationshipPair[] | undefined,
  nodes: Array<Pick<JourneyNode, 'nodeId'>>,
): RelationshipPair[] {
  const nodeIds = new Set(nodes.map((node) => node.nodeId));
  const seenPairIds = new Set<string>();
  const normalized: RelationshipPair[] = [];

  for (const pair of relationships ?? []) {
    if (!isValidRelationshipPairShape(pair, nodeIds) || seenPairIds.has(pair.pairId)) {
      continue;
    }

    seenPairIds.add(pair.pairId);
    normalized.push(pair);
  }

  return normalized;
}

export function normalizeGraphLayout(layout?: Partial<GraphLayoutSnapshot> | null): GraphLayoutSnapshot {
  return {
    nodeAnchors: Object.fromEntries(
      Object.entries(layout?.nodeAnchors ?? {}).map(([nodeId, anchor]) => [
        nodeId,
        {
          x: typeof anchor?.x === 'number' ? anchor.x : 0,
          y: typeof anchor?.y === 'number' ? anchor.y : 0,
          z: typeof anchor?.z === 'number' ? anchor.z : 0,
        },
      ])
    ),
  };
}

export function graphLayoutOffsets(layout?: GraphLayoutSnapshot | null): Record<string, GraphNodeOffset> {
  return Object.fromEntries(
    Object.entries(normalizeGraphLayout(layout).nodeAnchors).map(([nodeId, anchor]) => [nodeId, { x: anchor.x, y: anchor.y, z: anchor.z }])
  );
}

export function combineNodeOffsetMaps(
  ...offsetMaps: Array<Record<string, GraphNodeOffset> | undefined>
): Record<string, GraphNodeOffset> {
  const combined: Record<string, GraphNodeOffset> = {};

  for (const offsets of offsetMaps) {
    if (!offsets) continue;

    for (const [nodeId, offset] of Object.entries(offsets)) {
      combined[nodeId] = {
        x: (combined[nodeId]?.x ?? 0) + offset.x,
        y: (combined[nodeId]?.y ?? 0) + offset.y,
        z: (combined[nodeId]?.z ?? 0) + (offset.z ?? 0),
      };
    }
  }

  return combined;
}

export function mergeGraphLayoutWithOffsets(
  layout: GraphLayoutSnapshot | undefined,
  offsets: Record<string, GraphNodeOffset>
): GraphLayoutSnapshot {
  const normalized = normalizeGraphLayout(layout);
  const nodeAnchors = { ...normalized.nodeAnchors };

  for (const [nodeId, offset] of Object.entries(offsets)) {
    if (offset.x === 0 && offset.y === 0 && (offset.z ?? 0) === 0) continue;

    const existing = nodeAnchors[nodeId] ?? { x: 0, y: 0, z: 0 };
    nodeAnchors[nodeId] = {
      x: existing.x + offset.x,
      y: existing.y + offset.y,
      z: existing.z + (offset.z ?? 0),
    };
  }

  return { nodeAnchors };
}

export function removeNodeFromGraphLayout(
  layout: GraphLayoutSnapshot | undefined | null,
  nodeId: string,
): GraphLayoutSnapshot {
  const normalized = normalizeGraphLayout(layout);
  if (!(nodeId in normalized.nodeAnchors)) {
    return normalized;
  }

  const nodeAnchors = { ...normalized.nodeAnchors };
  delete nodeAnchors[nodeId];
  return { nodeAnchors };
}

function isValidRelationshipPairShape(pair: RelationshipPair, nodeIds: Set<string>): boolean {
  if (!pair || typeof pair.pairId !== 'string' || pair.endpoints.length !== 2 || pair.directions.length !== 2) {
    return false;
  }

  const [firstNodeId, secondNodeId] = pair.endpoints;
  if (firstNodeId === secondNodeId || !nodeIds.has(firstNodeId) || !nodeIds.has(secondNodeId)) {
    return false;
  }

  if (pair.pairId !== [firstNodeId, secondNodeId].sort((left, right) => left.localeCompare(right)).join('::')) {
    return false;
  }

  const expectedDirections = new Set([`${firstNodeId}->${secondNodeId}`, `${secondNodeId}->${firstNodeId}`]);
  if (!pair.directions.every((direction) => expectedDirections.has(`${direction.sourceNodeId}->${direction.targetNodeId}`))) {
    return false;
  }

  return pair.directions.some((direction) => direction.labels.length > 0);
}
